import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useLastMapUrl, { useHasMapHistory } from "../../hooks/useLastMapUrl";
import {
  GroupedMenuDisplay,
  getDisplayableMenuItems,
} from "./MenuDisplay";
import {
  CenteredContainer,
  CenteredContent,
} from "../layout/restaurantDetails/restaurantDetailsStyles";
import {
  LeftDiv,
  SpaceApartDiv,
} from "../layout/restaurantBuilder/restaurantBuilderLegacy/RestaurantCreateBuild";

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1976d2;
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 16px;

  &:hover {
    text-decoration: underline;
  }
`;

export const formatCuisine = (str) =>
  str.replace(/\b\w/g, (l) => l.toUpperCase());

export const getDirectionsLink = (restaurant) => {
  const formattedAddress =
    restaurant.address?.formatted_address ||
    (typeof restaurant.address === "string" ? restaurant.address : null);

  if (restaurant.googleId) {
    const destination = encodeURIComponent(restaurant.name);
    return {
      url: `https://www.google.com/maps/dir/?api=1&destination_place_id=${restaurant.googleId}&destination=${destination}`,
      text: formattedAddress || "Directions",
    };
  }

  if (formattedAddress) {
    const destination = encodeURIComponent(
      `${restaurant.name}, ${formattedAddress}`
    );
    return {
      url: `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
      text: formattedAddress,
    };
  }

  if (restaurant.geoJson?.coordinates) {
    return {
      url: `https://www.google.com/maps/dir/?api=1&destination=${restaurant.geoJson.coordinates[1]},${restaurant.geoJson.coordinates[0]}`,
      text: "Directions",
    };
  }

  return null;
};

/**
 * Shared layout for restaurant details views.
 *
 * Props:
 *  - restaurant: restaurant object (required)
 *  - headerExtra: ReactNode rendered after the name (e.g. badges)
 *  - beforeMenu: ReactNode rendered between info row and menu (e.g. hero image)
 *  - menuContent: ReactNode to replace the default GroupedMenuDisplay
 *  - afterMenu: ReactNode rendered after the menu (e.g. sources, markdown)
 *  - searchQuery: string for highlighting in menu items
 *  - onDirectionsClick: callback when directions link is clicked
 *  - onWebsiteClick: callback when website link is clicked
 *  - children: ReactNode rendered at the bottom (e.g. action bar)
 */
const RestaurantDetailsLayout = ({
  restaurant,
  headerExtra,
  beforeMenu,
  menuContent,
  afterMenu,
  searchQuery,
  onDirectionsClick,
  onWebsiteClick,
  children,
}) => {
  const lastMapUrl = useLastMapUrl();
  const hasMapHistory = useHasMapHistory();

  if (!restaurant) return null;

  const cuisines = restaurant.cuisine?.filter(Boolean) || [];
  const directions = getDirectionsLink(restaurant);

  const eventSlug = restaurant.event?.slug;
  const backTo =
    !hasMapHistory && restaurant.eventOnly && eventSlug
      ? `/events/${eventSlug}`
      : lastMapUrl;

  return (
    <>
      <BackLink to={backTo} style={{ padding: "16px 16px 0" }}>
        <i className="fas fa-arrow-left" />
        Back to results
      </BackLink>
      <CenteredContainer>
        <CenteredContent>
          <LeftDiv>
          <h2>
            {restaurant.name}
            {headerExtra}
          </h2>

          {beforeMenu}

          <SpaceApartDiv style={{ marginTop: "8px", marginBottom: "8px" }}>
            {cuisines.length > 0 && (
              <p style={{ margin: 0 }}>
                <b>Cuisines:</b>{" "}
                {cuisines.map(formatCuisine).join(", ")}
              </p>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "4px",
              }}
            >
              {directions && (
                <a
                  href={directions.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "none" }}
                  onClick={onDirectionsClick}
                >
                  <i
                    className="fas fa-directions"
                    style={{ marginRight: "4px", fontSize: "0.9em" }}
                  />
                  {directions.text}
                </a>
              )}
              {restaurant.website && (
                <a
                  href={
                    restaurant.website.startsWith("http")
                      ? restaurant.website
                      : `https://${restaurant.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "none" }}
                  onClick={onWebsiteClick}
                >
                  <i
                    className="fas fa-globe"
                    style={{ marginRight: "4px", fontSize: "0.9em" }}
                  />
                  {restaurant.website
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")}
                  <i
                    className="fas fa-external-link-alt"
                    style={{ marginLeft: "4px", fontSize: "0.8em" }}
                  />
                </a>
              )}
            </div>
          </SpaceApartDiv>
        </LeftDiv>

        {menuContent || (
          <GroupedMenuDisplay
            items={getDisplayableMenuItems(restaurant)}
            searchQuery={searchQuery}
          />
        )}

        {afterMenu}
        {children}
      </CenteredContent>
    </CenteredContainer>
    </>
  );
};

export default RestaurantDetailsLayout;
